'use client';

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clientApi } from '@/lib/api/client';
import type { HeatmapSensitivityRow } from '@/lib/heatmap/constants';
import { orgTierNames } from '@/lib/heatmap/org-tier';

export interface CompanyOrgStructureValue {
  stakeholderId: string;
  value: string;
  parentId?: string;
}

export interface CompanyOrgStructureLevel {
  level?: string;
  values: CompanyOrgStructureValue[];
}

/**
 * A tenant-configured extra column on the Audience > Individuals grid.
 *
 * `machineName` is the ACTUAL persisted field name on `falcon_individuals_table`
 * and is always one of `customIndividualsField1|2|3`. Slots come from that fixed
 * pool and freed slots are REUSED, so an entry's position in the array does NOT
 * imply its slot number — a company whose second field was deleted persists
 * `customIndividualsField1` and `customIndividualsField3`.
 *
 * Always key columns off `machineName`, never off the array index.
 */
export interface CompanyCustomIndividualsField {
  machineName: string;
  label: string;
}

/** One tenant-defined activity type: the category, and the name it is picked by. */
export interface CompanyActivityCustomType {
  type: string;
  intervention: string;
}

/**
 * One tenant-defined impact type: the name an impact is filed under, and the
 * prompt shown beneath it on the impact form.
 *
 * This array is a denormalised copy — the row an impact's `impactTypeId` points
 * at lives in `falcon_impact_types`. Settings > Impacts writes both.
 */
export interface CompanyImpactType {
  type: string;
  helper: string;
}

/** `GET /company` already returns the whole company document (README D13) — this
 * just types the two fields that were missing: who's an admin, and license
 * assignment. Shape matches falcon-be's `LicenseInfo` schema exactly. */
export interface CompanyLicenseInfo {
  total: number;
  inUse: number;
  users: string[];
}

/**
 * One pulse-check question on the company document.
 *
 * `question` really can be missing: two rows in production have no `question`
 * key at all, so the settings table renders an empty field rather than assuming
 * a string.
 */
export interface CompanySurveyQuestion {
  question?: string;
  /** The change-phase step, for a readiness question. */
  phase?: number;
  numberOfQuestion?: number;
  /** The Focus, for an adoption question. */
  type?: string;
}

export interface Company {
  _id: string;
  additionalCompanyStructure?: CompanyOrgStructureLevel[];
  /**
   * Ordered org-level names, e.g. ['Business Unit', 'Team']. POSITIONAL:
   * `orgStructure[i]` labels the persisted `ORG_LEVEL_{i + 1}` field.
   */
  orgStructure?: string[];
  /** User ids granted admin rights on this company (v1: `company.admins`). */
  admins?: string[];
  fullLicenses?: CompanyLicenseInfo;
  readOnlyLicenses?: CompanyLicenseInfo;
  /** Tenant-configured extra Individuals columns (max 3). NOT positional. */
  customIndividualsFields?: CompanyCustomIndividualsField[];
  /**
   * Ordered change-phase labels. POSITIONAL and 1-BASED: an activity's
   * `changePhase: n` is labelled by `activityColumns[n - 1]`.
   * Used by the initiatives Activities page and settings UI to label phases
   * and edit phase names. The All Activities page reads `AllActivity.changePhaseName`
   * from the server instead.
   */
  activityColumns?: string[];
  /** Tenant's own word for "Portfolio", used to label the filter. */
  portfolioFieldCustomName?: string;
  /** Tenant's own word for "Function". */
  functionFieldCustomName?: string;
  /**
   * User-selected date format (`dd/mm/yy` | `mm/dd/yy`); consumed by `DateInput`
   * via `useDateFormat` (`@/lib/utils/format-date`), which resolves it with
   * `resolveDateFormat` (`@/lib/utils/date-format`).
   */
  dateFormat?: string;
  /**
   * IANA zone the company's scheduled sends are anchored to, e.g.
   * `Australia/Sydney`. Absent means UTC. Set in Settings > General.
   */
  /** Company logo shown in the app header and on exports. */
  logoUrl?: string;
  /** "From" name on outbound pulse-check and survey email. */
  emailSenderDisplayName?: string;
  /**
   * The function catalogue, in display order. Mirrored into `falcon_functions`
   * — the array is what the pickers list, the collection is what initiatives
   * point at. Settings > General keeps the two in step.
   */
  function?: string[];
  /** Whether the Function field is offered on initiatives at all. */
  functionField?: boolean;
  /**
   * Whether the Portfolio field is offered on initiatives, and Portfolio
   * columns/filters/panels are shown across the app at all. Always `true` as
   * read through `useCompany` — Portfolio can no longer be switched off
   * (MTFM-26), whatever an older company document still stores.
   */
  portfolioField?: boolean;
  /** The initiative-type catalogue, in display order. */
  projectTypes?: string[];
  /** Whether the Initiative type field is offered on initiatives at all. */
  projectTypesField?: boolean;
  /**
   * The checklist names every new initiative is seeded with, in the order the
   * checklist renders. Edited in Settings > Initiative.
   */
  deliverablesNames?: string[];
  /** The categories the risk form offers, in display order. */
  riskCategories?: string[];
  /**
   * The tenant's own activity types. `type` is the category it files under,
   * `intervention` is the name shown when picking an activity type.
   */
  activityCustomTypes?: CompanyActivityCustomType[];
  /** The impact-type catalogue, in display order. */
  impactsTypes?: CompanyImpactType[];
  /**
   * The three impact-magnitude prompts shown on the impact form. POSITIONAL:
   * High, Medium, Low — the levels are fixed, so this is a bare array every
   * reader indexes into. See `IMPACT_LEVEL_LABELS`.
   */
  impactLevelTextHelper?: string[];
  /**
   * The readiness pulse-check questions, one per change phase. Edited in
   * Settings > Assessment.
   *
   * KEYED BY `phase`, NOT positional: `phase` is the change-phase step the
   * question is asked at (`activityColumns[phase - 1]` names it), and the stored
   * order is v1's `[1, 4, 5, 2, 3]`. Read them through
   * `sortSurveyQuestionsByPhase` rather than in array order.
   */
  surveyFeedbackStakeholders?: CompanySurveyQuestion[];
  /**
   * The adoption pulse-check questions, one per Focus. Edited in
   * Settings > Assessment.
   *
   * `type` is the Focus, and it joins to an adoption pulse-check intervention BY
   * NAME — which is why nothing in this app lets it be edited.
   */
  adoptionSurvey?: CompanySurveyQuestion[];
  /** Whether full-licence users, not only admins, may manage custom groups. */
  allowFullAccessUsersManageCustomGroups?: boolean;
  activityBurderHeatmapSensitivity?: HeatmapSensitivityRow[];
  settings?: {
    reportsSensitivity?: {
      activityBurderHeatmap?: {
        total?: HeatmapSensitivityRow[];
      };
    };
  };
}

export interface CompanyResponse {
  company: Company;
  user: unknown;
}

/**
 * Current user's company (mirrors the Angular CompanyService).
 *
 * `enabled` mirrors `useCurrentUser`'s: a guest page has no session, so
 * `/company` 401s there. Callers on a route that can be reached signed out
 * must pass `enabled: !isGuestPath(pathname)` — see `CompanyDateFormatProvider`,
 * which is mounted in the root shell and therefore runs on every route.
 */
export function useCompany({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['company'],
    queryFn: async () => {
      const response = await clientApi<CompanyResponse>('company');
      // Portfolio is always on (MTFM-26): Settings no longer offers a switch
      // for it, so a company that turned it off before the switch was removed
      // would otherwise lose every Portfolio column, filter and field with no
      // way back. Forced here, once, so no reader has to know.
      if (!response?.company) return response;
      return { ...response, company: { ...response.company, portfolioField: true } };
    },
    staleTime: 5 * 60_000,
    retry: false,
    enabled,
  });
}

/**
 * The company's org-structure tiers as dropdown options, where each option's
 * `id` is the 0-indexed level the report APIs take as `orgLevel`.
 *
 * `orgStructure` holds the company's own tier names; `additionalCompanyStructure`
 * is the fallback for companies that never named them.
 */
export function useOrgTierOptions(): Array<{ id: string; label: string }> {
  const { data } = useCompany();
  const company = data?.company;

  return useMemo(
    // Counted from `additionalCompanyStructure`, never from `orgStructure` --
    // see `lib/heatmap/org-tier.ts`. Counting the name list offered a tier with
    // no values behind it: on the dev company that is an "Org Level 5" the
    // backend already reads as the Location view. It matters most in Settings >
    // Impacts, where these tiers are the ROWS of the threshold grid, so a
    // phantom tier meant a threshold row for a level that does not exist.
    () => orgTierNames(company).map((name, index) => ({ id: String(index), label: name })),
    [company],
  );
}
